import { useEffect, useRef, useCallback } from 'react'
import { useWeb3React } from '@web3-react/core'
import { injected } from '../connectors'
import { getStoredWalletAddress, storeWalletAddress, clearStoredWalletAddress } from '../utils/walletStorage'

// 监控连接状态并尝试重新连接
export function useConnectionMonitor() {
  const { active, activate, account, chainId } = useWeb3React()
  const intervalRef = useRef<NodeJS.Timeout>()
  const permissionRequestedRef = useRef(false)

  // 请求 MetaMask 权限
  const requestPermission = useCallback(async () => {
    if (permissionRequestedRef.current) {
      return
    }

    try {
      permissionRequestedRef.current = true
      const accounts = await (window.ethereum as any).request({ method: 'eth_requestAccounts' })

      if (accounts && accounts.length > 0) {
        // 存储账户信息
        if (chainId) {
          storeWalletAddress(accounts[0], chainId)
        }
        // 重新激活连接
        try {
          await activate(injected, undefined, true)
        } catch (error) {
          // Connection re-activation failed
        }
      }
    } catch (error) {
      // Permission request failed
    } finally {
      // 10秒后允许重新请求权限
      setTimeout(() => {
        permissionRequestedRef.current = false
      }, 10000)
    }
  }, [activate, chainId])

  // 检查 MetaMask 账户并尝试重新连接
  const checkAndReconnect = useCallback(async () => {
    if (!window.ethereum || reconnectingRef.current) return

    try {
      // 获取 MetaMask 中的账户
      const accounts = await (window.ethereum as any).request({ method: 'eth_accounts' })

      if (accounts && accounts.length > 0) {
        const metamaskAccount = accounts[0]

        // 如果获取到账户，立即存储
        if (chainId) {
          storeWalletAddress(metamaskAccount, chainId)
        }

        // 如果我们的状态与 MetaMask 不一致，重新连接
        if (!account || account !== metamaskAccount) {
          if (!reconnectingRef.current) {
            reconnectingRef.current = true
            await activate(injected, undefined, true)
            // 等待一段时间后重置标志
            setTimeout(() => {
              reconnectingRef.current = false
            }, 5000)
          }
        }
      } else {
        // MetaMask 返回空数组，但有存储的地址，主动请求权限
        const storedAddress = getStoredWalletAddress()
        if (storedAddress && !permissionRequestedRef.current) {
          await requestPermission()
        } else if (account && !permissionRequestedRef.current) {
          // 我们有账户但 MetaMask 返回空数组，请求权限
          await requestPermission()
        }
      }
    } catch (error) {
      reconnectingRef.current = false
    }
  }, [account, activate, chainId, requestPermission])

  // 避免重复重新连接的标志
  const reconnectingRef = useRef(false)

  // 使用存储的地址快速连接
  const quickConnectWithStoredAddress = useCallback(async () => {
    if (!window.ethereum) return false

    try {
      const storedAddress = getStoredWalletAddress()
      if (!storedAddress) return false

      // 检查 MetaMask 是否有这个地址
      const accounts = await (window.ethereum as any).request({ method: 'eth_accounts' })

      if (accounts && accounts.includes(storedAddress)) {
        await activate(injected, undefined, true)
        return true
      } else {
        // 存储的地址不在 MetaMask 中，但不立即清除存储
        // 可能是 MetaMask 还没有完全初始化
        return false
      }
    } catch (error) {
      return false
    }
  }, [activate])

  // 定期检查连接状态
  useEffect(() => {
    if (!active && window.ethereum) {
      // 连接不活跃时，每3秒检查一次
      intervalRef.current = setInterval(checkAndReconnect, 3000)
    } else {
      // 连接活跃时，清除定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [active, checkAndReconnect])

  // 监听 MetaMask 事件
  useEffect(() => {
    if (!window.ethereum || !window.ethereum.on) return

    const handleAccountsChanged = async (accounts: string[]) => {
      if (accounts.length > 0) {
        // 账户变化时重新连接并更新存储
        if (!account || account !== accounts[0]) {
          await activate(injected, undefined, true)
        }
        // 更新本地存储
        if (chainId) {
          storeWalletAddress(accounts[0], chainId)
        }
      } else {
        // 账户被移除，但可能是 MetaMask 还没有完全初始化
        // 延迟一段时间后再清除存储，避免页面刷新时误删
        setTimeout(() => {
          // 再次检查是否真的没有账户
          if (window.ethereum) {
            ;(window.ethereum as any)
              .request({ method: 'eth_accounts' })
              .then((accounts: string[]) => {
                if (!accounts || accounts.length === 0) {
                  clearStoredWalletAddress()
                }
              })
              .catch(() => {
                // 如果检查失败，也清除存储
                clearStoredWalletAddress()
              })
          }
        }, 2000) // 等待2秒后再检查
      }
    }

    const handleChainChanged = (chainId: string | number) => {
      // 链变化时重新连接
      setTimeout(() => {
        activate(injected, undefined, true)
      }, 1000)
    }

    const handleConnect = (connectInfo: any) => {
      // 连接时重新激活
      setTimeout(() => {
        activate(injected, undefined, true)
      }, 1000)
    }

    const handleDisconnect = (error: Error) => {
      // 断开连接时延迟清除存储，避免页面刷新时误删
      setTimeout(() => {
        clearStoredWalletAddress()
      }, 1000)
    }

    // 添加事件监听器
    window.ethereum.on('accountsChanged', handleAccountsChanged)
    window.ethereum.on('chainChanged', handleChainChanged)
    window.ethereum.on('connect', handleConnect)
    window.ethereum.on('disconnect', handleDisconnect)

    // 清理函数
    return () => {
      if (window.ethereum && window.ethereum.removeListener) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
        window.ethereum.removeListener('chainChanged', handleChainChanged)
        window.ethereum.removeListener('connect', handleConnect)
        window.ethereum.removeListener('disconnect', handleDisconnect)
      }
    }
  }, [account, activate, chainId])

  // 页面加载时激活 provider 并尝试连接
  useEffect(() => {
    if (!active && window.ethereum) {
      // 页面加载时，只激活 provider，不强制连接
      setTimeout(async () => {
        try {
          // 1. 激活 provider
          await (window.ethereum as any).request({ method: 'eth_chainId' })

          // 2. 检查是否有存储的地址，如果有就尝试快速连接
          const storedAddress = getStoredWalletAddress()
          if (storedAddress) {
            await quickConnectWithStoredAddress()
          }
        } catch (error) {
          // Provider activation failed
        }
      }, 500)
    }
  }, [active, quickConnectWithStoredAddress]) // 只在组件挂载时执行一次

  // 用户交互时检查连接
  useEffect(() => {
    const handleUserInteraction = () => {
      // 只在用户交互时检查连接，避免频繁检查
      if (!active || !account) {
        checkAndReconnect()
      }
    }

    // 监听用户点击事件
    document.addEventListener('click', handleUserInteraction)

    return () => {
      document.removeEventListener('click', handleUserInteraction)
    }
  }, [active, account, checkAndReconnect])

  // 监听余额查询失败事件，主动请求权限
  useEffect(() => {
    const handleRequestPermission = (event: CustomEvent) => {
      if (event.detail?.address && event.detail?.chainId) {
        // 检查存储的地址是否匹配
        const storedAddress = getStoredWalletAddress()
        if (storedAddress === event.detail.address) {
          requestPermission()
        }
      }
    }

    // 监听自定义事件
    window.addEventListener('requestMetaMaskPermission', handleRequestPermission as EventListener)

    return () => {
      window.removeEventListener('requestMetaMaskPermission', handleRequestPermission as EventListener)
    }
  }, [requestPermission])

  // 监控账户变化，更新本地存储
  useEffect(() => {
    if (account && chainId) {
      // 账户连接成功，存储到本地
      storeWalletAddress(account, chainId)
    }
  }, [account, chainId])
}
